import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import parse from 'html-react-parser';
import { marked } from 'marked';
import { useParams } from 'react-router-dom';

import { formatContent, getReferenceReplacer } from './kim-message';
import { Permission } from '../../../../../../../../../../../backend/src/shared/action-registry';
import {
  ArtifactBlock as ArtifactBlockType,
  ArtifactContent,
  ReferenceType,
} from '../../../../../../../../../../../backend/src/types/types';
import { KIM_MARKED_OPTIONS } from '../../utils/kim-markdown';
import { useTogglePublishedArtifact } from '@/api/mutations/useTogglePublishedArtifact';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

export function ArtifactBlock({
  id,
  content,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  collapsed = false,
  version,
  isDownloading,
  artifactsById,
  isOwnThread,
}: {
  id: string;
  content: ArtifactContent[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: () => void;
  collapsed?: boolean;
  version?: number;
  isDownloading?: boolean;
  artifactsById?: Map<string, ArtifactBlockType & { published: boolean | undefined }>;
  isOwnThread?: boolean;
}) {
  const { matterId, subsetId } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isCollapsed = collapsed && !isExpanded;

  const artifact = artifactsById?.get(id);
  const isPublished = artifact?.published === true;

  const { mutate: togglePublished, isLoading: isPublishing } = useTogglePublishedArtifact(matterId, subsetId);

  const handleTogglePublish = () => {
    const lastDash = id.lastIndexOf('-');
    const artifactId = id.slice(0, lastDash);
    const versionNumber = Number(id.slice(lastDash + 1));
    togglePublished({ artifactId, versionNumber });
  };

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const markdown = content
      .map((c) => (c.type === 'text' ? c.value : ''))
      .join('')
      .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '');
    const html = marked.parse(markdown, KIM_MARKED_OPTIONS) as string;
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([markdown], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      }),
    ]);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const rendered = useMemo(() => {
    if (!content?.length) return null;

    const formattedContent = formatContent(content);
    const html = marked.parse(formattedContent, KIM_MARKED_OPTIONS) as string;

    return parse(html, {
      replace: getReferenceReplacer(referenceIndexMap, onReferenceClick, { hideTableActions: true }),
    });
  }, [content, referenceIndexMap, onReferenceClick]);

  return (
    <div
      className="prose prose-sm prose-headings:my-3 prose-strong:font-semibold prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-900 prose-headings:font-semibold mx-2 mt-8 max-w-none scroll-mt-8 overflow-hidden rounded-lg border bg-white"
      {...(id && { 'data-artifact-id': id })}
    >
      {rendered && (
        <div className="relative">
          {/* Version number */}
          {version !== undefined && (
            <div className="absolute top-0 left-0 m-2 rounded-md border bg-gray-50 px-2 py-0.5 font-sans text-[11px] text-gray-500">
              Version {version}
            </div>
          )}

          <div className="absolute top-0 right-0 z-10 m-2 mr-3 flex">
            {/* Copy button */}
            <Button onClick={handleCopy} className="gap-1.75 px-1.5 font-sans font-normal" size="sm" variant="ghost">
              <AnimatePresence mode="wait">
                {copied ? (
                  <motion.span
                    key="check"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.1 }}
                  >
                    <Icon className="lucide--check" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="copy"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.1 }}
                  >
                    <Icon className="lucide--copy text-[11px]" />
                  </motion.span>
                )}
              </AnimatePresence>
              <span className="mt-px">Copy</span>
            </Button>

            {/* Download button */}
            {onDownloadClick && (
              <PermissionGate
                permission={Permission.ThreadDownload}
                uiMode="disabled"
                disabledMessage="You don't have permission to download work product"
              >
                <Button
                  disabled={isDownloading}
                  onClick={onDownloadClick}
                  className="gap-1.75 px-1.5 font-sans font-normal"
                  size="sm"
                  variant="ghost"
                >
                  <Icon className="lucide--download" />
                  <span className="mt-px">Download</span>
                </Button>
              </PermissionGate>
            )}

            {/* Publish button — only shown for the thread owner + perms */}
            {isOwnThread && artifact?.published !== undefined && (
              <PermissionGate
                permission={Permission.ThreadPublish}
                uiMode="disabled"
                disabledMessage="You don't have permission to publish work product"
              >
                <Button
                  disabled={isPublishing}
                  onClick={handleTogglePublish}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  className="gap-1.75 px-1.5 font-sans font-normal"
                  size="sm"
                  variant="ghost"
                >
                  {isPublishing ? (
                    <Icon className="lucide--loader animate-spin" />
                  ) : (
                    <Icon
                      className={
                        isPublished
                          ? isHovered
                            ? 'lucide--user-round-x text-teal-500'
                            : 'lucide--users-round text-teal-500'
                          : 'lucide--users-round'
                      }
                    />
                  )}
                  <span className={cn(isPublished ? 'w-14' : 'w-10', 'mt-px duration-150')}>
                    {isPublished ? (isHovered ? 'Unpublish' : 'Published') : 'Publish'}
                  </span>
                </Button>
              </PermissionGate>
            )}

            {/* Publish chip - only shown for other users */}
            {!isOwnThread && isPublished && (
              <Chip className="border border-teal-300 bg-teal-50 py-0.5 font-sans text-[11px] text-teal-600">
                <Icon className="lucide--users-round" />
                <span className="mt-px">Published</span>
              </Chip>
            )}
          </div>

          <div className={cn('px-16 pt-12 pb-16', isCollapsed && 'max-h-48 overflow-hidden mask-b-from-30%')}>
            <div className="prose prose-sm prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-li:my-0.75 max-w-none [&_ol_p]:my-3 [&_th]:bg-transparent [&_tr:hover]:bg-transparent">
              {rendered}
            </div>
          </div>

          {isCollapsed && (
            <button
              onClick={() => setIsExpanded(true)}
              className="group absolute bottom-0 flex w-full justify-center pb-3 focus:outline-none"
            >
              <div className="flex items-center gap-0.75 rounded-full border bg-white py-1 pr-1.5 pl-2.5 font-sans text-xs text-gray-500 shadow-lg/2 ring-teal-500 ring-offset-2 ring-offset-transparent transition-colors duration-150 group-hover:border-gray-300 group-hover:bg-gray-50 group-hover:text-gray-600 group-focus-visible:ring-1 group-focus-visible:ring-offset-white">
                <span>Show more</span>
                <Icon className="lucide--chevron-down mt-px text-sm" />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
